/**
 * Turns what a user types into the AI Tags search box into a Mongo filter.
 *
 * The filter is built client-side and travels through the grid's existing
 * `tableGlobalSearchQuery` plumbing, so no new API endpoint is involved — the server
 * merges it with the column filters exactly as it does the old regex query.
 *
 * Three input forms are supported:
 *
 *   zen midday          free words, ANDed
 *   style:zen           scoped to one attribute
 *   "swimming pool"     quoted phrase, matched as a unit
 *   25464               a bare number still finds a record by id
 *
 * Why a token array rather than $text: production runs on AWS DocumentDB, which
 * supports neither text indexes nor $text queries. `aiTags.searchTokens` holds whole
 * tag values plus the individual words of those tags and of the description, so a
 * single indexed `$all` covers every searchable surface at once.
 *
 * `$all` gives AND semantics directly — which is the whole reason multi-word search
 * works here when it does not in the old box, which ORs everything and so returns MORE
 * results the more words you type.
 *
 * Singular/plural is handled at WRITE time — the worker stores both forms of every
 * word — so this side stays a literal lookup and "pergolas" still finds "pergola".
 * Nothing here needs to know about stemming, which is what keeps the browser and the
 * server from having to agree on a normalisation algorithm.
 *
 * Limitation worth knowing: a quoted phrase is matched as "all of these words present",
 * not as an adjacent phrase, and only number is normalised, not tense ("running" will
 * not match "run").
 */

export interface AiTagQueryOptions {
  /** Extra fields to fall back to when the AI index has nothing to say. */
  fallbackFields?: string[];
}

const ATTRIBUTE_PREFIX = 'aiTags.attributes.';

/** Splits on whitespace but keeps "quoted phrases" together. */
export function tokenizeAiQuery(input: string): string[] {
  if (!input) return [];

  // Order matters: a quoted value attached to a key (room_type:"living room") has to
  // be matched before the bare \S+ branch, or the greedy run swallows the opening
  // quote and the phrase splits at the space.
  const tokens = input.match(/[A-Za-z_][A-Za-z0-9_]*:"[^"]*"|"[^"]*"|\S+/g) || [];

  return tokens
    .map((token) => token.trim())
    .filter((token) => token.length > 0);
}

function stripQuotes(value: string): string {
  return value.replace(/^"|"$/g, '').trim();
}

/** Escapes a value destined for a RegExp so a stray ( or [ cannot throw. */
function escapeRegex(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

export interface ParsedAiQuery {
  /** key:value pairs, scoped to a single attribute each. */
  scoped: Array<{ key: string; value: string }>;
  /** Free words and phrases, all required. */
  terms: string[];
  /** A bare number, which should still resolve by record id. */
  numericId: number | null;
}

export function parseAiQuery(input: string): ParsedAiQuery {
  const scoped: Array<{ key: string; value: string }> = [];
  const terms: string[] = [];
  let numericId: number | null = null;

  for (const rawToken of tokenizeAiQuery(input)) {
    // key:value — but not a bare ":" and not a quoted phrase containing a colon.
    const scopedMatch = !rawToken.startsWith('"') && rawToken.match(/^([A-Za-z_][A-Za-z0-9_]*):(.+)$/);

    if (scopedMatch) {
      const value = stripQuotes(scopedMatch[2]);
      if (value) scoped.push({ key: scopedMatch[1], value });
      continue;
    }

    const token = stripQuotes(rawToken);
    if (!token) continue;

    // A lone number is far more likely to be an id than a tag.
    if (terms.length === 0 && scoped.length === 0 && /^\d+$/.test(token)) {
      numericId = Number(token);
      continue;
    }

    terms.push(token);
  }

  return { scoped, terms, numericId };
}

/**
 * Builds the Mongo filter. Returns {} for empty input so the grid falls back to
 * showing everything rather than matching nothing.
 *
 * Deliberately emits a FLAT object rather than wrapping conditions in `$and`.
 * The server merges this into the column-filter query with a deep merge that treats
 * arrays as scalars — so a top-level `$and` here would overwrite the `$and` the column
 * filters build, silently dropping one or the other. Distinct top-level keys merge
 * cleanly and mean the same thing (implicit AND).
 */
export function buildAiTagQuery(input: string): any {
  const { scoped, terms, numericId } = parseAiQuery(input);

  if (numericId !== null && !terms.length && !scoped.length) {
    return { id: numericId };
  }

  const query: any = {};

  if (terms.length) {
    // A phrase is split into its words: searchTokens holds whole tag values too, but
    // a phrase drawn from the description only ever exists there word by word.
    const tokens = [
      ...new Set(
        terms
          .flatMap((term) => term.toLowerCase().split(/[^a-z0-9']+/))
          .map((token) => token.replace(/^'+|'+$/g, ''))
          .filter((token) => token.length > 0),
      ),
    ];

    if (tokens.length) {
      // $all is the AND. One top-level key, so it merges cleanly with column filters.
      query['aiTags.searchTokens'] = { $all: tokens };
    }
  }

  // Group by attribute so repeating a key (style:zen style:rustic) means "has both"
  // rather than the second silently replacing the first.
  const byKey = new Map<string, string[]>();
  for (const { key, value } of scoped) {
    const path = `${ATTRIBUTE_PREFIX}${key}`;
    byKey.set(path, [...(byKey.get(path) || []), value]);
  }

  for (const [path, values] of byKey) {
    // Anchored + case-insensitive so `style:zen` matches the stored title-cased "Zen"
    // without also matching "Zen Garden". Expressed as {$regex, $options} strings
    // rather than RegExp literals because this query is POSTed as JSON, and a RegExp
    // instance serializes to {} — which would match everything.
    const matchers = values.map((value) => ({ $regex: `^${escapeRegex(value)}$`, $options: 'i' }));

    query[path] =
      matchers.length === 1
        ? matchers[0]
        : // Repeating a key means "has all of these", which over an array field needs
          // $all + $elemMatch; a bare $all of operator objects is not valid Mongo.
          { $all: matchers.map((matcher) => ({ $elemMatch: matcher })) };
  }

  return query;
}

/**
 * The name/fileName query the grid used before AI search existed.
 *
 * Kept reachable because AI search only finds assets that have been through
 * /v1/process — until the back catalogue is covered, that is a minority of records,
 * and a curator who types an exact file name still needs to find it.
 */
export function buildFallbackQuery(input: string, fallbackFields: string[] = []): any {
  if (!input) return {};

  const conditions: any[] = [{ name: { $regex: escapeRegex(input), $options: 'i' } }];

  if (/^\d+$/.test(input)) {
    conditions.push({ id: Number(input) });
  } else {
    conditions.push(
      ...fallbackFields.map((field) => ({
        [field]: { $regex: escapeRegex(input), $options: 'i' },
      })),
    );
  }

  return { $or: conditions };
}
